import { useState, useEffect } from 'react';
import { useDataProvider, useStore } from 'react-admin';
import {
	Card,
	CardContent,
	Typography,
	Box,
	LinearProgress,
	Grid,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	CircularProgress,
	Alert
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SyncIcon from '@mui/icons-material/Sync';
import WarningIcon from '@mui/icons-material/Warning';

/**
 * Indexing Statistics Dashboard Widget
 * Shows real-time indexing status for documents in the current group
 */
export const IndexingStats = () => {
	const dataProvider = useDataProvider();
	const [activeGroupId] = useStore('activeGroupId');
	const [stats, setStats] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const fetchStats = async () => {
		if (!activeGroupId) {
			setLoading(false);
			return;
		}

		try {
			const data = await dataProvider.getIndexingStats(activeGroupId);
			setStats(data);
			setError(null);
		} catch (err) {
			console.error('Error fetching indexing stats:', err);
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchStats();

		// Auto-refresh every 10 seconds if there are documents in progress
		const interval = setInterval(() => {
			if (stats?.summary?.indexing > 0) {
				fetchStats();
			}
		}, 10000);

		return () => clearInterval(interval);
	}, [activeGroupId, stats?.summary?.indexing]);

	if (loading) {
		return (
			<Card>
				<CardContent>
					<Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
						<CircularProgress />
					</Box>
				</CardContent>
			</Card>
		);
	}

	if (error) {
		return (
			<Card>
				<CardContent>
					<Alert severity="error">Failed to load indexing statistics: {error}</Alert>
				</CardContent>
			</Card>
		);
	}

	if (!stats || !activeGroupId) {
		return (
			<Card>
				<CardContent>
					<Alert severity="info">Select a group to view indexing statistics</Alert>
				</CardContent>
			</Card>
		);
	}

	const { summary, byStatus, topics } = stats;

	return (
		<Card>
			<CardContent>
				<Typography variant="h5" gutterBottom>
					Document Indexing Status
				</Typography>

				{/* Summary Section */}
				<Box mb={3}>
					<Grid container spacing={2}>
						<Grid item xs={12} md={3}>
							<StatCard
								label="Total Documents"
								value={summary.total}
								icon={<CheckCircleIcon />}
								color="primary"
							/>
						</Grid>
						<Grid item xs={12} md={3}>
							<StatCard
								label="Indexed"
								value={summary.indexed}
								icon={<CheckCircleIcon />}
								color="success"
							/>
						</Grid>
						<Grid item xs={12} md={3}>
							<StatCard
								label="In Progress"
								value={summary.indexing}
								icon={<SyncIcon />}
								color="info"
								spinning={summary.indexing > 0}
							/>
						</Grid>
						{ ( summary.failed ) ?
						<Grid item xs={12} md={3}>
							<StatCard
								label="Failed"
								value={summary.failed}
								icon={<ErrorIcon />}
								color="error"
							/>
						</Grid>
						: '' }
					</Grid>
				</Box>

				{/* Health Progress Bar */}
				<Box mb={3}>
					<Typography variant="body2" color="textSecondary" gutterBottom>
						Indexing Health: {summary.healthPercentage}%
					</Typography>
					<LinearProgress
						variant="determinate"
						value={summary.healthPercentage}
						color={summary.healthPercentage >= 90 ? 'success' : summary.healthPercentage >= 70 ? 'warning' : 'error'}
						sx={{ height: 10, borderRadius: 5 }}
					/>
				</Box>

				{/* Status Breakdown */}
				<Box mb={3}>
					<Typography variant="h6" gutterBottom>
						Status Breakdown
					</Typography>
					<Box display="flex" gap={1} flexWrap="wrap">
						{(byStatus?.uploaded || 0) > 0 && (
							<Chip label={`Uploaded: ${byStatus.uploaded}`} color="default" size="small" />
						)}
						{(byStatus?.extracted || 0) > 0 && (
							<Chip label={`Extracted: ${byStatus.extracted}`} color="success" size="small" />
						)}
						{(byStatus?.indexing || 0) > 0 && (
							<Chip label={`Indexing: ${byStatus.indexing}`} color="info" size="small" icon={<SyncIcon />} />
						)}
						{(byStatus?.reindexing || 0) > 0 && (
							<Chip label={`Re-indexing: ${byStatus.reindexing}`} color="info" size="small" icon={<SyncIcon />} />
						)}
						{(byStatus?.indexed || 0) > 0 && (
							<Chip label={`Indexed: ${byStatus.indexed}`} color="primary" size="small" icon={<CheckCircleIcon />} />
						)}
						{(byStatus?.extraction_failed || 0) > 0 && (
							<Chip label={`Extraction Failed: ${byStatus.extraction_failed}`} color="error" size="small" icon={<ErrorIcon />} />
						)}
						{(byStatus?.indexing_failed || 0) > 0 && (
							<Chip label={`Indexing Failed: ${byStatus.indexing_failed}`} color="error" size="small" icon={<ErrorIcon />} />
						)}
						{(byStatus?.unsupported_format || 0) > 0 && (
							<Chip label={`Unsupported: ${byStatus.unsupported_format}`} color="warning" size="small" icon={<WarningIcon />} />
						)}
					</Box>
				</Box>

				{/* Topic Breakdown Table */}
				{topics && topics.length > 0 && (
					<Box>
						<Typography variant="h6" gutterBottom>
							By Topic
						</Typography>
						<TableContainer component={Paper} variant="outlined">
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Topic</TableCell>
										<TableCell align="right">Total</TableCell>
										<TableCell align="right">Indexed</TableCell>
										<TableCell align="right">In Progress</TableCell>
										<TableCell align="right">Failed</TableCell>
										<TableCell align="right">Pending</TableCell>
										<TableCell align="right">Health</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{topics.map((topic) => {
										const health = topic.total > 0 ? Math.round((topic.indexed / topic.total) * 100) : 0;
										return (
											<TableRow key={topic.id}>
												<TableCell>{topic.name}</TableCell>
												<TableCell align="right">{topic.total}</TableCell>
												<TableCell align="right">{topic.indexed}</TableCell>
												<TableCell align="right">{topic.indexing}</TableCell>
												<TableCell align="right">{topic.failed}</TableCell>
												<TableCell align="right">{topic.pending}</TableCell>
												<TableCell align="right">
													<Chip
														label={`${health}%`}
														size="small"
														color={health >= 90 ? 'success' : health >= 70 ? 'warning' : 'error'}
													/>
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</TableContainer>
					</Box>
				)}

				{/* Note about Azure propagation delay */}
				{summary.indexing > 0 && (
					<Box mt={2}>
						<Alert severity="info" icon={<HourglassEmptyIcon />}>
							Documents are being indexed. Azure AI Search statistics may take 1-2 minutes to update after indexing completes.
						</Alert>
					</Box>
				)}
			</CardContent>
		</Card>
	);
};

/**
 * Stat Card Component
 */
const StatCard = ({ label, value, icon, color, spinning }) => (
	<Paper elevation={2} sx={{ p: 2 }}>
		<Box display="flex" alignItems="center" justifyContent="space-between">
			<Box>
				<Typography variant="body2" color="textSecondary">
					{label}
				</Typography>
				<Typography variant="h4" color={`${color}.main`}>
					{value}
				</Typography>
			</Box>
			<Box
				sx={{
					color: `${color}.main`,
					animation: spinning ? 'spin 2s linear infinite' : 'none',
					'@keyframes spin': {
						'0%': { transform: 'rotate(0deg)' },
						'100%': { transform: 'rotate(360deg)' }
					}
				}}
			>
				{icon}
			</Box>
		</Box>
	</Paper>
);

export default IndexingStats;

